/**
 * Validates the structure of a solution's phases array
 * @param {Array} phases - The phases array to validate
 * @returns {{isValid: boolean, errors: Array<string>}} - Validation result
 */
export const validateSolutionPhases = (phases) => {
  const errors = [];
  
  if (!Array.isArray(phases)) {
    errors.push('Phases must be an array');
    return { isValid: false, errors };
  }
  
  if (phases.length === 0) {
    errors.push('At least one phase is required');
  }
  
  if (phases.length > 10) {
    errors.push('Maximum of 10 phases allowed');
  }
  
  phases.forEach((phase, index) => {
    if (!phase || typeof phase !== 'object') {
      errors.push(`Phase ${index + 1}: Must be an object`);
      return;
    }
    
    if (!phase.id) {
      errors.push(`Phase ${index + 1}: Missing required field 'id'`);
    }
    
    if (!phase.objective) {
      errors.push(`Phase ${index + 1}: Missing required field 'objective'`);
    }
    
    if (!Array.isArray(phase.tasks)) {
      errors.push(`Phase ${index + 1}: 'tasks' must be an array`);
    } else if (phase.tasks.length === 0) {
      errors.push(`Phase ${index + 1}: At least one task is required`);
    } else {
      phase.tasks.forEach((task, taskIndex) => {
        if (!task || typeof task !== 'object') {
          errors.push(`Phase ${index + 1}, Task ${taskIndex + 1}: Must be an object`);
          return;
        }
        
        if (!task.id) {
          errors.push(`Phase ${index + 1}, Task ${taskIndex + 1}: Missing required field 'id'`);
        }
        
        // Check for either 'content' or 'description' field
        if (!task.content && !task.description) {
          errors.push(`Phase ${index + 1}, Task ${taskIndex + 1}: Task must have either 'content' or 'description' field`);
        }
      });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates a complete solution object
 * @param {Object} solution - The solution to validate
 * @returns {{isValid: boolean, errors: Array<string>}} - Validation result
 */
export const validateSolution = (solution) => {
  const errors = [];
  const solutionMode = solution.solutionMode || 'detailed';
  const baseRequiredFields = ['title', 'type', 'creatorId', 'creatorUsername', 'postId'];
  
  // Check base required fields
  baseRequiredFields.forEach(field => {
    if (!solution[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate based on solution mode
  if (solutionMode === 'detailed') {
    // For detailed solutions, phases are required
    if (!solution.phases) {
      errors.push('Missing required field: phases');
    } else {
      const phaseValidation = validateSolutionPhases(solution.phases);
      if (!phaseValidation.isValid) {
        errors.push(...phaseValidation.errors);
      }
    }
  } else if (solutionMode === 'general') {
    // For general solutions, content is required
    if (!solution.content || !solution.content.trim()) {
      errors.push('General solutions must have content');
    }
    // Content length validation
    if (solution.content && solution.content.length > 10000) {
      errors.push('Solution content must be less than 10,000 characters');
    }
  } else {
    errors.push(`Invalid solution mode: ${solutionMode}. Must be 'detailed' or 'general'`);
  }
  
  // Validate creatorProfession if it exists
  if ('creatorProfession' in solution && solution.creatorProfession !== null) {
    if (typeof solution.creatorProfession !== 'string') {
      errors.push('creatorProfession must be a string or null');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
